/**
 * Draft Post Link Recommendations Page
 * Shows draft HubSpot blog posts and allows generating link recommendations before publishing.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Link2,
  FileEdit,
  RefreshCw,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Tag,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  BarChart2,
  Lock,
  Search,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  History,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../hooks/useSite';
import {
  draftLinksApi, type DraftPost, type DraftLinksResult,
  postLinksApi, type AnalyzedPostCandidate, type PostLinksResult,
  type PostLinksRecommendation, type PostLinksDirection, type PostLinksScope,
  type PostLinksHistoryEntry,
} from '../../lib/dashboard-api';

// ============================================
// TYPES
// ============================================

type PostStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'error';

interface PostAnalysisState {
  status: PostStatus;
  savedCount: number;
  structureAnalysis: Record<string, unknown> | null;
  categoryRecommendation: Record<string, unknown> | null;
  error: string | null;
  errorIsTransient: boolean;
}

type PostAnalysisMap = Record<string, PostAnalysisState>;

// ============================================
// HELPERS
// ============================================

function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function extractStructure(result: DraftLinksResult): Record<string, unknown> | null {
  const r = result as unknown as Record<string, unknown>;
  const d = (r.data as Record<string, unknown> | undefined);
  const dd = d?.data as Record<string, unknown> | undefined;
  return (dd?.structure_analysis ?? d?.structure_analysis ?? r.structure_analysis ?? null) as Record<string, unknown> | null;
}

function extractCategoryRec(result: DraftLinksResult): Record<string, unknown> | null {
  const r = result as unknown as Record<string, unknown>;
  const d = (r.data as Record<string, unknown> | undefined);
  const dd = d?.data as Record<string, unknown> | undefined;
  return (dd?.primary_category_recommendation ?? d?.primary_category_recommendation ?? r.primary_category_recommendation ?? null) as Record<string, unknown> | null;
}

// A refused prepare call is a 4xx, so axios throws and the { error, message }
// body is only reachable through err.response.data.
function serverErrorFields(err: unknown): { code: string; msg: string } | null {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  if (!data || typeof data.error !== 'string') return null;
  return { code: data.error, msg: typeof data.message === 'string' ? data.message : '' };
}

function errorMessageFor(code: string, msg: string): { text: string; transient: boolean } {
  const map: Record<string, { text: string; transient: boolean }> = {
    NO_LICENSE:       { text: 'No active license. Please activate your license in Settings.', transient: false },
    REQUIRES_PRO:     { text: 'Draft Post Link Recommendations require a PRO license.', transient: false },
    NO_BASELINE:      { text: 'A baseline analysis must be completed before using this feature.', transient: false },
    NOT_A_DRAFT:      { text: 'This post was published before the request completed. Refresh the list.', transient: false },
    EMPTY_CONTENT:    { text: 'Add more content to this post in HubSpot before requesting recommendations.', transient: false },
    NO_PLS_OBJECTS:   { text: 'No published posts matched this category — complete a baseline analysis first.', transient: false },
    AI_FAILED:        { text: 'AI processing failed. Please try again.', transient: true },
    RATE_LIMIT:       { text: 'Rate limit reached. Please wait a moment and try again.', transient: true },
    TIMEOUT:          { text: 'Request timed out. Please try again.', transient: true },
    PARSE_FAILED:     { text: 'Could not parse AI response. Please try again.', transient: true },
  };
  return map[code] ?? { text: msg || 'An unexpected error occurred.', transient: false };
}

// ============================================
// POST LINKS (Search by Title/URL) — ERROR MAP
// ============================================

function postLinksErrorMessageFor(code: string, msg: string): { text: string; transient: boolean } {
  const map: Record<string, { text: string; transient: boolean }> = {
    NO_LICENSE:                  { text: 'No active license. Please activate your license in Settings.', transient: false },
    REQUIRES_PRO:                { text: 'Post Link Recommendations require a PRO license.', transient: false },
    NOT_ANALYZED:                { text: 'This post must be included in a baseline or incremental analysis before you can get link recommendations for it.', transient: false },
    CATEGORY_REQUIRED:           { text: 'This post has multiple categories — please select one.', transient: false },
    POST_NOT_FOUND:              { text: 'Post not found in HubSpot.', transient: false },
    MISSING_TENANT_INFO:         { text: 'Missing license credentials. Please re-activate your license.', transient: false },
    MISSING_API_KEY:             { text: 'Missing Claude API key. Please re-activate your license.', transient: false },
    MISSING_REQUIRED_FIELDS:     { text: 'Post data was incomplete. Please try again.', transient: true },
    EMPTY_CONTENT:               { text: 'This post has no content or content is too short.', transient: false },
    CONTENT_TOO_LARGE:           { text: 'This post is too long to process (max 50,000 characters).', transient: false },
    INVALID_CATEGORY:            { text: 'A category is required.', transient: false },
    INVALID_DIRECTION:           { text: 'Invalid direction selected.', transient: false },
    INVALID_LINK_SCOPE:          { text: 'Invalid link scope selected.', transient: false },
    NO_CLSC:                     { text: 'No category analysis found for this category — run a baseline analysis first.', transient: false },
    NO_PLS_OBJECTS:              { text: 'No published posts found in this category — complete a baseline analysis first.', transient: false },
    INBOUND_REQUIRES_PUBLISHED:  { text: 'Inbound recommendations require this post to be published and already analyzed.', transient: false },
    INBOUND_REQUIRES_URL:        { text: 'Could not resolve this post\u2019s published URL, which is required for inbound recommendations.', transient: false },
    TIMEOUT:                     { text: 'Request timed out. This can take up to 5 minutes — please try again.', transient: true },
  };
  return map[code] ?? { text: msg || 'An unexpected error occurred.', transient: false };
}

// ============================================
// STRUCTURE ANALYSIS PANEL
// ============================================

function StructurePanel({ analysis, category }: {
  analysis: Record<string, unknown> | null;
  category: Record<string, unknown> | null;
}) {
  const [open, setOpen] = useState(false);
  if (!analysis) return null;

  const score = typeof analysis.overall_score === 'number' ? analysis.overall_score : null;
  const recs = Array.isArray(analysis.recommendations) ? analysis.recommendations as string[] : [];
  const catName = (category as Record<string, unknown> | null)?.name as string | undefined;

  return (
    <div className="mt-3 rounded border border-white/10 bg-white/5 text-xs">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-white/70 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-1.5 font-medium">
          <BarChart2 className="w-3.5 h-3.5" />
          Heading Structure Analysis
          {score !== null && (
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
              score >= 80 ? 'bg-green-500/20 text-green-400' :
              score >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {score}/100
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 text-white/60">
          {catName && (
            <p className="text-[#ffdc18]/80">
              Suggested primary category: <strong className="text-[#ffdc18]">{catName}</strong>
            </p>
          )}
          {recs.length > 0 ? (
            <ul className="list-disc list-inside space-y-1">
              {recs.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          ) : (
            <p>No specific heading recommendations.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// DRAFT POST ROW
// ============================================

interface PostRowProps {
  post: DraftPost;
  portalId: string;
  state: PostAnalysisState;
  onGetRecs: (postId: string, category: string) => void;
}

function PostRow({ post, portalId, state, onGetRecs }: PostRowProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    post.tags.length === 1 ? post.tags[0].id : ''
  );

  const hubspotEditorUrl = `https://app.hubspot.com/blog/${portalId}/editor/${post.id}/content`;
  const isLoading = state.status === 'loading';
  const hasError = state.status === 'error';
  const hasSuccess = state.status === 'success';

  const selectedCategory = post.tags.find(t => t.id === selectedCategoryId);

  function handleGetRecs() {
    if (!selectedCategory) return;
    onGetRecs(post.id, selectedCategory.name);
  }

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-white truncate">{post.title}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {post.updatedAt && (
              <span className="text-xs text-white/40">Updated {formatDate(post.updatedAt)}</span>
            )}
            {post.tags.map(t => (
              <span key={t.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-[10px] text-white/60">
                <Tag className="w-2.5 h-2.5" />
                {t.name}
              </span>
            ))}
          </div>
        </div>
        <a
          href={hubspotEditorUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs text-white/50 hover:text-white/80 border border-white/10 hover:border-white/20 rounded transition-colors"
        >
          Edit in HubSpot
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* State A: no eligible tags */}
      {!post.hasEligibleTags && (
        <div className="mt-3 flex items-start gap-2 text-xs text-amber-400/80 bg-amber-400/10 border border-amber-400/20 rounded p-2.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            No category tag assigned. Assign a content category in HubSpot, then refresh this list.
          </span>
        </div>
      )}

      {/* State C: existing pending recs button */}
      {post.hasEligibleTags && post.pendingRecsCount > 0 && state.status === 'idle' && (
        <div className="mt-3">
          <Link
            to={`/links?postId=${post.id}&postTitle=${encodeURIComponent(post.title)}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#ffdc18] text-[#ffdc18] hover:bg-[#ffdc18]/10 rounded transition-colors"
          >
            <Link2 className="w-3.5 h-3.5" />
            View {post.pendingRecsCount} Link Recommendation{post.pendingRecsCount !== 1 ? 's' : ''}
          </Link>
        </div>
      )}

      {/* State B: tagged, no recs yet — show get recs UI */}
      {post.hasEligibleTags && post.pendingRecsCount === 0 && state.status === 'idle' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {post.tags.length > 1 && (
            <select
              value={selectedCategoryId}
              onChange={e => setSelectedCategoryId(e.target.value)}
              className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1 text-white/80 focus:outline-none focus:border-[#ffdc18]/50"
            >
              <option value="" disabled>Select category…</option>
              {post.tags.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            disabled={!selectedCategory}
            onClick={handleGetRecs}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#ffdc18] text-black rounded hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Link2 className="w-3.5 h-3.5" />
            Get Pre-Publish Link Recommendations
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Analysing post and generating recommendations… (this takes 10–30s)
        </div>
      )}

      {/* Error state */}
      {hasError && state.error && (
        <div className="mt-3 space-y-2">
          <div className="flex items-start gap-2 text-xs text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-2.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{state.error}</span>
          </div>
          {state.errorIsTransient && selectedCategory && (
            <button
              type="button"
              onClick={handleGetRecs}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/20 text-white/70 hover:text-white rounded transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Try Again
            </button>
          )}
        </div>
      )}

      {/* Success state */}
      {hasSuccess && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 text-xs text-green-400/80">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {state.savedCount > 0
              ? `${state.savedCount} recommendation${state.savedCount !== 1 ? 's' : ''} saved`
              : 'Analysis complete — no new recommendations found'}
          </div>
          {state.savedCount > 0 && (
            <Link
              to={`/links?postId=${post.id}&postTitle=${encodeURIComponent(post.title)}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#ffdc18] text-[#ffdc18] hover:bg-[#ffdc18]/10 rounded transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" />
              View {state.savedCount} Link Recommendation{state.savedCount !== 1 ? 's' : ''}
            </Link>
          )}
          <StructurePanel
            analysis={state.structureAnalysis}
            category={state.categoryRecommendation}
          />
        </div>
      )}
    </div>
  );
}

// ============================================
// POST LINKS SEARCH TAB (analyzed posts only)
// ============================================

function directionBadgeClasses(direction: 'inbound' | 'outbound') {
  return direction === 'inbound'
    ? 'bg-[#60a5fa]/15 text-[#60a5fa]'
    : 'bg-[#4ade80]/15 text-[#4ade80]';
}

function linkTypeBadgeClasses(linkType: string) {
  return linkType === 'intra_category'
    ? 'bg-purple-400/15 text-purple-300'
    : 'bg-amber-400/15 text-amber-300';
}

function linkTypeLabel(linkType: string) {
  return linkType === 'intra_category' ? 'Intra-Category' : 'Inter-Category';
}

/**
 * Single recommendation card — shared between the "just generated" results
 * panel and the "Past Searches" expanded detail view, so both look identical.
 */
function RecommendationCard({ rec }: { rec: PostLinksRecommendation }) {
  return (
    <div className="rounded border border-white/10 bg-white/5 p-3 text-xs space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${directionBadgeClasses(rec.direction)}`}>
          {rec.direction === 'inbound'
            ? <ArrowDownToLine className="w-3 h-3" />
            : <ArrowUpFromLine className="w-3 h-3" />}
          {rec.direction === 'inbound' ? 'Inbound' : 'Outbound'}
        </span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${linkTypeBadgeClasses(rec.link_type)}`}>
          <ArrowLeftRight className="w-3 h-3" />
          {linkTypeLabel(rec.link_type)}
        </span>
        <span className="text-white/40">Relevance {rec.relevance_score}/10 · Priority {rec.priority_score}/10</span>
      </div>
      <p className="text-white/80 font-medium">{rec.counterpart_post_title}</p>
      <p className="text-white/50">Anchor: "{rec.anchor_text}"</p>
      <p className="text-white/40">{rec.reason}</p>
    </div>
  );
}

function formatDateTime(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function directionLabel(direction: PostLinksDirection | null) {
  if (direction === 'inbound') return 'Inbound only';
  if (direction === 'outbound') return 'Outbound only';
  return 'Inbound & Outbound';
}

function scopeLabel(scope: PostLinksScope | null) {
  if (scope === 'intra') return 'Intra-Category only';
  if (scope === 'inter') return 'Inter-Category only';
  return 'Intra & Inter-Category';
}

// ============================================
// PAST SEARCHES (history of post-links lookups)
// ============================================

interface PastSearchesProps {
  siteId: string;
  refreshToken: number;
}

function PastSearches({ siteId, refreshToken }: PastSearchesProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searches, setSearches] = useState<PostLinksHistoryEntry[]>([]);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, PostLinksRecommendation[]>>({});

  const loadHistory = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await postLinksApi.getHistory(siteId);
      setSearches(res.searches || []);
    } catch (err) {
      console.error('getHistory error:', err);
      setError('Failed to load past searches.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, refreshToken]);

  async function toggleExpand(postId: string) {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      return;
    }
    setExpandedPostId(postId);
    if (!detailCache[postId]) {
      setDetailLoading(postId);
      setDetailError(null);
      try {
        const res = await postLinksApi.getHistoryDetail(siteId, postId);
        setDetailCache(prev => ({ ...prev, [postId]: res.recommendations || [] }));
      } catch (err) {
        console.error('getHistoryDetail error:', err);
        setDetailError('Failed to load recommendations for this post.');
      } finally {
        setDetailLoading(null);
      }
    }
  }

  if (loading && searches.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-white/40 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading past searches…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 text-xs text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-2.5">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <p className="text-xs text-white/30 py-2">
        No past searches yet — results you generate above will appear here so you can revisit them later.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {searches.map(s => {
        const isExpanded = expandedPostId === s.postId;
        return (
          <div key={s.postId} className="rounded-lg border border-white/10 bg-white/5 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(s.postId)}
              className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{s.postTitle || `Post ${s.postId}`}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-white/40">
                  <span>{formatDateTime(s.generatedAt)}</span>
                  <span>·</span>
                  <span>{s.totalRecommendations} recommendation{s.totalRecommendations !== 1 ? 's' : ''}</span>
                  {s.category && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white/50">
                      <Tag className="w-2.5 h-2.5" />
                      {s.category}
                    </span>
                  )}
                  <span className="text-white/30">{directionLabel(s.direction)} · {scopeLabel(s.linkScope)}</span>
                </div>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />}
            </button>

            {isExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t border-white/10 pt-3">
                <Link
                  to={`/links?postId=${s.postId}&postTitle=${encodeURIComponent(s.postTitle || '')}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#ffdc18] text-[#ffdc18] hover:bg-[#ffdc18]/10 rounded transition-colors"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  View &amp; Implement on Links Page
                </Link>

                {detailLoading === s.postId && (
                  <div className="flex items-center gap-2 text-xs text-white/40 py-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading recommendations…
                  </div>
                )}

                {detailError && detailLoading !== s.postId && (
                  <div className="flex items-start gap-2 text-xs text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-2.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{detailError}</span>
                  </div>
                )}

                {detailCache[s.postId]?.map((rec, i) => (
                  <RecommendationCard key={i} rec={rec} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface PostSearchTabProps {
  siteId: string;
  hasBaseline: boolean | null;
  /** Deep-link (?q=): pre-fill the search box and run the search on mount. */
  initialQuery?: string;
  /** Deep-link (?postId=): auto-select this post if it is among the results. */
  initialPostId?: string;
}

function PostSearchTab({ siteId, hasBaseline, initialQuery, initialPostId }: PostSearchTabProps) {
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  const [query, setQuery] = useState(initialQuery ?? '');
  const deepLinkRan = useRef(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AnalyzedPostCandidate[]>([]);

  const [selected, setSelected] = useState<AnalyzedPostCandidate | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [direction, setDirection] = useState<PostLinksDirection>('both');
  const [linkScope, setLinkScope] = useState<PostLinksScope>('both');

  const [status, setStatus] = useState<PostStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorIsTransient, setErrorIsTransient] = useState(false);
  const [recs, setRecs] = useState<PostLinksRecommendation[]>([]);
  const [savedCount, setSavedCount] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);

  async function handleSearch(text: string = query, autoSelectPostId?: string) {
    const trimmed = text.trim();
    if (trimmed.length < 2) return;
    setSearching(true);
    setSearchError(null);
    setSearched(true);
    setSelected(null);
    setStatus('idle');
    try {
      const res = await postLinksApi.search(siteId, trimmed);
      const found = res.candidates || [];
      setCandidates(found);
      const match = autoSelectPostId ? found.find(c => String(c.postId) === autoSelectPostId) : undefined;
      if (match) selectCandidate(match);
    } catch (err) {
      console.error('postLinks search error:', err);
      setSearchError('Failed to search posts. Please try again.');
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  }

  useEffect(() => {
    if (deepLinkRan.current || !siteId || !initialQuery) return;
    deepLinkRan.current = true;
    handleSearch(initialQuery, initialPostId);
    // Runs once per deep link; handleSearch is recreated every render.
  }, [siteId, initialQuery, initialPostId]);

  function selectCandidate(candidate: AnalyzedPostCandidate) {
    setSelected(candidate);
    setSelectedCategory(candidate.categories.length === 1 ? candidate.categories[0] : '');
    setStatus('idle');
    setError(null);
    setRecs([]);
    setWarnings([]);
  }

  function backToResults() {
    setSelected(null);
    setStatus('idle');
    setError(null);
    setRecs([]);
    setWarnings([]);
  }

  async function handleGetRecs() {
    if (!selected) return;
    if (selected.categories.length > 1 && !selectedCategory) return;

    setStatus('loading');
    setError(null);

    try {
      const category = selectedCategory || selected.categories[0];
      const prepare = await postLinksApi.prepare(siteId, selected.postId, direction, linkScope, category);

      if (!prepare.success) {
        const err = (prepare as unknown as Record<string, unknown>).error as string || 'UNKNOWN';
        const msg = (prepare as unknown as Record<string, unknown>).message as string || '';
        const { text, transient } = postLinksErrorMessageFor(err, msg);
        setStatus('error'); setError(text); setErrorIsTransient(transient);
        return;
      }

      const bkaResp = await fetch(prepare.bkaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': prepare.credentials.licenseKey,
          'X-Domain': prepare.credentials.domain,
          'X-Instance-Key': prepare.credentials.instanceKey,
          'X-API-Key': prepare.credentials.claudeApiKey,
        },
        body: JSON.stringify(prepare.bkaRequestBody),
        signal: AbortSignal.timeout(300_000), // BKA spec: up to 5 minutes
      });

      if (!bkaResp.ok) {
        const errorData = await bkaResp.json().catch(() => ({})) as Record<string, unknown>;
        const code = (errorData.code as string) || '';
        const msg = (errorData.message as string) || (errorData.error as string) || `BKA error ${bkaResp.status}`;
        const { text, transient } = postLinksErrorMessageFor(code, msg);
        setStatus('error'); setError(text); setErrorIsTransient(transient);
        return;
      }

      const result: PostLinksResult = await bkaResp.json();

      if (!result.success) {
        const code = (result as unknown as Record<string, unknown>).code as string || '';
        const msg = (result as unknown as Record<string, unknown>).message as string || '';
        const { text, transient } = postLinksErrorMessageFor(code, msg);
        setStatus('error'); setError(text); setErrorIsTransient(transient);
        return;
      }

      postLinksApi.saveResult(
        prepare.siteId, selected.postId, selected.title,
        prepare.bkaRequestBody.category, result, prepare.contentMd5,
        prepare.bkaRequestBody.url, direction, linkScope
      ).then(() => {
        setHistoryRefreshToken(t => t + 1);
      }).catch(err => {
        console.warn('postLinks saveResult failed (non-fatal):', err);
      });

      setRecs(result.recommendations || []);
      setSavedCount(typeof result.saved_count === 'number' ? result.saved_count : (result.recommendations?.length ?? 0));
      setWarnings(result.warnings || []);
      setStatus('success');

    } catch (err: unknown) {
      console.error('handleGetRecs (post-links) error:', err);
      const server = serverErrorFields(err);
      const msg = server?.msg || (err instanceof Error ? err.message : String(err));
      const isTimeout = !server && (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('abort'));
      const { text, transient } = postLinksErrorMessageFor(server?.code || (isTimeout ? 'TIMEOUT' : ''), msg);
      setStatus('error'); setError(text); setErrorIsTransient(transient);
    }
  }

  const isLoading = status === 'loading';

  return (
    <div className="space-y-4">
      {/* Search box */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          placeholder="Enter a post title or URL…"
          className="flex-1 min-w-[240px] text-sm bg-white/10 border border-white/20 rounded px-3 py-2 text-white/90 focus:outline-none focus:border-[#ffdc18]/50"
        />
        <button
          type="button"
          onClick={() => handleSearch()}
          disabled={query.trim().length < 2 || searching}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[#ffdc18] text-black rounded hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Search
        </button>
      </div>

      {hasBaseline === false && (
        <div className="flex items-start gap-2 text-xs text-white/50 bg-white/5 border border-white/10 rounded p-2.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#ffdc18]" />
          <span>No baseline analysis has been completed yet for this site — no posts will match until one has run.</span>
        </div>
      )}

      {searchError && (
        <div className="flex items-start gap-2 text-sm text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{searchError}</span>
        </div>
      )}

      {/* No matches — must be analyzed first */}
      {searched && !searching && !searchError && candidates.length === 0 && (
        <div className="rounded-lg border border-[#ffdc18]/30 bg-[#ffdc18]/5 p-4 space-y-2">
          <p className="text-sm font-semibold text-white">No analyzed post found</p>
          <p className="text-xs text-white/50">
            This post must be included in a baseline or incremental analysis before you can get link recommendations for it.
            Run an analysis from the Dashboard, then search again.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-xs text-[#ffdc18] hover:text-[#ffdc18]/80 underline underline-offset-2"
          >
            Go to Dashboard to Run Analysis →
          </Link>
        </div>
      )}

      {/* Candidate list */}
      {!selected && candidates.length > 0 && (
        <div className="space-y-2">
          {candidates.map(c => (
            <button
              key={c.postId}
              type="button"
              onClick={() => selectCandidate(c)}
              className="w-full text-left rounded-lg border border-white/10 bg-white/5 hover:border-[#ffdc18]/40 hover:bg-white/10 p-3 transition-colors"
            >
              <p className="text-sm font-medium text-white truncate">{c.title}</p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-xs text-white/40 truncate">{c.url}</span>
                {c.categories.map(cat => (
                  <span key={cat} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-[10px] text-white/60">
                    <Tag className="w-2.5 h-2.5" />
                    {cat}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected candidate — configuration + action */}
      {selected && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-white">{selected.title}</h3>
              <p className="text-xs text-white/40 mt-0.5">{selected.url}</p>
            </div>
            <button
              type="button"
              onClick={backToResults}
              className="flex-shrink-0 text-xs text-white/50 hover:text-white/80 underline underline-offset-2"
            >
              Change post
            </button>
          </div>

          {status === 'idle' && (
            <div className="flex flex-wrap items-end gap-3">
              {selected.categories.length > 1 && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-white/50">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white/80 focus:outline-none focus:border-[#ffdc18]/50"
                  >
                    <option value="" disabled>Select category…</option>
                    {selected.categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs text-white/50">Direction</label>
                <select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as PostLinksDirection)}
                  className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white/80 focus:outline-none focus:border-[#ffdc18]/50"
                >
                  <option value="both">Inbound &amp; Outbound</option>
                  <option value="inbound">Inbound only</option>
                  <option value="outbound">Outbound only</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-white/50">Link Scope</label>
                <select
                  value={linkScope}
                  onChange={(e) => setLinkScope(e.target.value as PostLinksScope)}
                  className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white/80 focus:outline-none focus:border-[#ffdc18]/50"
                >
                  <option value="both">Intra &amp; Inter-Category</option>
                  <option value="intra">Intra-Category only</option>
                  <option value="inter">Inter-Category only</option>
                </select>
              </div>

              <button
                type="button"
                disabled={selected.categories.length > 1 && !selectedCategory}
                onClick={handleGetRecs}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#ffdc18] text-black rounded hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Link2 className="w-3.5 h-3.5" />
                Get Link Recommendations
              </button>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-white/50">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Generating recommendations… (this can take up to 5 minutes)
            </div>
          )}

          {status === 'error' && error && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-2.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
              {errorIsTransient && (
                <button
                  type="button"
                  onClick={handleGetRecs}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/20 text-white/70 hover:text-white rounded transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Try Again
                </button>
              )}
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-green-400/80">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {savedCount > 0
                  ? `${savedCount} recommendation${savedCount !== 1 ? 's' : ''} saved`
                  : 'Analysis complete — no new recommendations found'}
              </div>

              {warnings.length > 0 && (
                <ul className="text-xs text-amber-400/80 list-disc list-inside space-y-0.5">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}

              {savedCount > 0 && (
                <Link
                  to={`/links?postId=${selected.postId}&postTitle=${encodeURIComponent(selected.title)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#ffdc18] text-[#ffdc18] hover:bg-[#ffdc18]/10 rounded transition-colors"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  View &amp; Implement on Links Page
                </Link>
              )}

              {recs.length > 0 && (
                <div className="space-y-2 mt-2">
                  {recs.map((rec, i) => <RecommendationCard key={i} rec={rec} />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Past Searches */}
      <div className="pt-4 border-t border-white/10">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-white/70 mb-2">
          <History className="w-4 h-4" />
          Past Searches
        </h2>
        <PastSearches siteId={siteId} refreshToken={historyRefreshToken} />
      </div>
    </div>
  );
}

// ============================================
// PAGE
// ============================================

export default function DraftLinksPage() {
  const { user } = useAuth();
  const { currentSite, isPro, isLoading: siteLoading } = useSite();

  const [posts, setPosts] = useState<DraftPost[]>([]);
  const [hasBaseline, setHasBaseline] = useState<boolean | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageErrorCode, setPageErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisMap, setAnalysisMap] = useState<PostAnalysisMap>({});
  // Deep link from the Dashboard's Connectivity Tier modal:
  // /draft-links?tab=search&siteId=..&postId=..&q=<post title>
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'drafts' | 'search'>(
    searchParams.get('tab') === 'search' ? 'search' : 'drafts'
  );

  const siteId = String(currentSite?.id ?? '');
  // Only auto-run the deep-linked search against the site it was created for.
  const linkSiteId = searchParams.get('siteId');
  const deepLinkMatchesSite = !linkSiteId || linkSiteId === siteId;
  const deepLinkQuery = deepLinkMatchesSite ? searchParams.get('q') ?? undefined : undefined;
  const deepLinkPostId = deepLinkMatchesSite ? searchParams.get('postId') ?? undefined : undefined;
  const portalId = String(currentSite?.platformId ?? '');

  // ---- Load draft posts ----
  const loadDraftPosts = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setPageError(null);
    setPageErrorCode(null);
    try {
      const res = await draftLinksApi.getDraftPosts(siteId);
      if (res.error === 'BLOG_NOT_CONFIGURED') {
        setPageErrorCode('BLOG_NOT_CONFIGURED');
        setPageError(res.message ?? 'HubSpot blog not configured.');
        setPosts([]);
        setHasBaseline(null);
      } else {
        setHasBaseline(res.hasBaseline ?? false);
        setPosts(res.posts ?? []);
        // Init analysis map for new posts
        setAnalysisMap(prev => {
          const next = { ...prev };
          (res.posts ?? []).forEach(p => {
            if (!next[p.id]) {
              next[p.id] = { status: 'idle', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: null, errorIsTransient: false };
            }
          });
          return next;
        });
      }
    } catch (err) {
      console.error('loadDraftPosts error:', err);
      setPageError('Failed to load draft posts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!siteLoading && siteId) loadDraftPosts();
  }, [siteLoading, siteId, loadDraftPosts]);

  // ---- Get recommendations for a post ----
  async function handleGetRecs(postId: string, category: string) {
    setAnalysisMap(prev => ({
      ...prev,
      [postId]: { status: 'loading', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: null, errorIsTransient: false }
    }));

    try {
      // Step 1: Prepare — get credentials + BKA URL from backend
      const prepare = await draftLinksApi.prepare(siteId, postId, category);

      if (!prepare.success) {
        const err = (prepare as unknown as Record<string, unknown>).error as string || 'UNKNOWN';
        const msg = (prepare as unknown as Record<string, unknown>).message as string || '';
        const { text, transient } = errorMessageFor(err, msg);
        setAnalysisMap(prev => ({
          ...prev,
          [postId]: { status: 'error', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: text, errorIsTransient: transient }
        }));
        return;
      }

      // Step 2: Call BKA directly from browser
      const bkaResp = await fetch(prepare.bkaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': prepare.credentials.licenseKey,
          'X-Domain': prepare.credentials.domain,
          'X-Instance-Key': prepare.credentials.instanceKey,
          'X-API-Key': prepare.credentials.claudeApiKey,
        },
        body: JSON.stringify(prepare.bkaRequestBody),
        signal: AbortSignal.timeout(120_000),
      });

      if (!bkaResp.ok) {
        const errorData = await bkaResp.json().catch(() => ({})) as Record<string, unknown>;
        const code = (errorData.code as string) || '';
        const msg = (errorData.message as string) || (errorData.error as string) || `BKA error ${bkaResp.status}`;
        const { text, transient } = errorMessageFor(code, msg);
        setAnalysisMap(prev => ({
          ...prev,
          [postId]: { status: 'error', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: text, errorIsTransient: transient }
        }));
        return;
      }

      const bkaResult: DraftLinksResult = await bkaResp.json();

      if (!bkaResult.success) {
        const code = (bkaResult as unknown as Record<string, unknown>).code as string || '';
        const msg = (bkaResult as unknown as Record<string, unknown>).message as string || '';
        const { text, transient } = errorMessageFor(code, msg);
        setAnalysisMap(prev => ({
          ...prev,
          [postId]: { status: 'error', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: text, errorIsTransient: transient }
        }));
        return;
      }

      // Step 3: Save recommendations + structure analysis to our DB (fire-and-forget)
      draftLinksApi.saveStructure(
        prepare.siteId, postId,
        prepare.bkaRequestBody.title,
        prepare.bkaRequestBody.category,
        bkaResult, prepare.contentMd5
      ).catch(err => {
        console.warn('saveDraftResult failed (non-fatal):', err);
      });

      // Step 4: Update UI state
      const savedCount = typeof bkaResult.saved_count === 'number' ? bkaResult.saved_count : (bkaResult.recommendations?.length ?? 0);
      const structureAnalysis = extractStructure(bkaResult);
      const categoryRecommendation = extractCategoryRec(bkaResult);

      setAnalysisMap(prev => ({
        ...prev,
        [postId]: {
          status: 'success',
          savedCount,
          structureAnalysis,
          categoryRecommendation,
          error: null,
          errorIsTransient: false,
        }
      }));

      // Update the post's pendingRecsCount in the list
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, pendingRecsCount: (p.pendingRecsCount || 0) + savedCount } : p
      ));

    } catch (err: unknown) {
      console.error('handleGetRecs error:', err);
      const server = serverErrorFields(err);
      const msg = server?.msg || (err instanceof Error ? err.message : String(err));
      const isTimeout = !server && (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('abort'));
      const { text, transient } = errorMessageFor(server?.code || (isTimeout ? 'TIMEOUT' : ''), msg);
      setAnalysisMap(prev => ({
        ...prev,
        [postId]: { status: 'error', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: text, errorIsTransient: transient }
      }));
    }
  }

  // ============================================
  // RENDER: guards
  // ============================================

  if (siteLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (!user || !currentSite) {
    return (
      <div className="p-6 text-white/50 text-sm">No site selected.</div>
    );
  }

  // PRO gate
  if (!isPro) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-6">
        <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center space-y-5">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#ffdc18]/10 border border-[#ffdc18]/20 mx-auto">
            <Lock className="w-5 h-5 text-[#ffdc18]" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-white">Upgrade to PRO to Unlock</h2>
            <p className="text-sm text-white/50 leading-relaxed">
              Draft Post Link Recommendations let you generate internal linking suggestions for
              unpublished posts before they go live — so every new post launches fully optimised.
            </p>
          </div>
          <ul className="text-left text-xs text-white/40 space-y-1.5 max-w-xs mx-auto">
            <li className="flex items-start gap-2">
              <span className="text-[#ffdc18] mt-0.5">✓</span>
              Pre-publish link recommendations per draft post
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ffdc18] mt-0.5">✓</span>
              Heading structure analysis &amp; category suggestions
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ffdc18] mt-0.5">✓</span>
              Separate from your 75-rec analysis quota
            </li>
          </ul>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold bg-[#ffdc18] text-black rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
          >
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: main
  // ============================================

  return (
    <div className="max-w-3xl mx-auto py-6 px-4 space-y-6">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <FileEdit className="w-5 h-5 text-[#ffdc18]" />
            Draft Post Link Recommendations
          </h1>
          <p className="text-xs text-white/40 mt-0.5">
            Generate internal link recommendations for draft posts before publishing, or search
            for an already-analyzed post to get fresh inbound/outbound recommendations.
          </p>
        </div>
        {activeTab === 'drafts' && (
          <button
            type="button"
            onClick={loadDraftPosts}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/20 rounded transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-white/10">
        <button
          type="button"
          onClick={() => setActiveTab('drafts')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'drafts'
              ? 'border-[#ffdc18] text-white'
              : 'border-transparent text-white/40 hover:text-white/70'
          }`}
        >
          Draft Posts
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('search')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'search'
              ? 'border-[#ffdc18] text-white'
              : 'border-transparent text-white/40 hover:text-white/70'
          }`}
        >
          Search by Title/URL
        </button>
      </div>

      {activeTab === 'search' && (
        <PostSearchTab
          key={`${siteId}:${deepLinkPostId ?? ''}`}
          siteId={siteId}
          hasBaseline={hasBaseline}
          initialQuery={deepLinkQuery}
          initialPostId={deepLinkPostId}
        />
      )}

      {activeTab === 'drafts' && (
      <>
      {/* BLOG_NOT_CONFIGURED error */}
      {pageErrorCode === 'BLOG_NOT_CONFIGURED' && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-4 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-300">Blog Not Configured</p>
              <p className="text-xs text-amber-400/70 mt-0.5">
                Your HubSpot blog isn't linked yet. Go to Settings and connect your blog to use this feature.
              </p>
            </div>
          </div>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2"
          >
            Open Settings →
          </Link>
        </div>
      )}

      {/* Generic page error */}
      {pageError && pageErrorCode !== 'BLOG_NOT_CONFIGURED' && (
        <div className="flex items-start gap-2 text-sm text-red-400/80 bg-red-400/10 border border-red-400/20 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{pageError}</span>
        </div>
      )}

      {/* No baseline blocking panel */}
      {hasBaseline === false && !pageErrorCode && (
        <div className="rounded-lg border border-[#ffdc18]/30 bg-[#ffdc18]/5 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#ffdc18] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white">Baseline Analysis Required</p>
              <p className="text-xs text-white/50 mt-1">
                Draft Post Link Recommendations use your existing published posts as link targets.
                Run a baseline analysis from the Dashboard first — this builds the database of published content
                the AI searches when finding internal link opportunities.
              </p>
            </div>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#ffdc18] text-black rounded hover:bg-[#ffdc18]/90 transition-colors"
          >
            Go to Dashboard to Run Analysis →
          </Link>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && posts.length === 0 && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4 animate-pulse">
              <div className="h-4 bg-white/10 rounded w-2/3 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !pageErrorCode && posts.length === 0 && hasBaseline !== null && (
        <div className="text-center py-12 text-white/30 text-sm space-y-2">
          <FileEdit className="w-8 h-8 mx-auto opacity-30" />
          <p>No draft posts found for this blog.</p>
          <p className="text-xs">Create a draft post in HubSpot, then refresh this page.</p>
        </div>
      )}

      {/* Post list */}
      {posts.length > 0 && (
        <div className="space-y-3">
          {posts.map(post => (
            <PostRow
              key={post.id}
              post={post}
              portalId={portalId}
              state={analysisMap[post.id] ?? { status: 'idle', savedCount: 0, structureAnalysis: null, categoryRecommendation: null, error: null, errorIsTransient: false }}
              onGetRecs={handleGetRecs}
            />
          ))}
        </div>
      )}

      {/* Footer note */}
      {posts.length > 0 && (
        <p className="text-xs text-white/25 text-center pb-2">
          Showing draft posts for your connected blog only. Published posts appear in the Links page.
        </p>
      )}
      </>
      )}
    </div>
  );
}
